import { ApplicationConfig, APP_INITIALIZER, PLATFORM_ID, importProvidersFrom, provideBrowserGlobalErrorListeners, provideZoneChangeDetection } from '@angular/core';
import { ALLOW_IMPERSONATION } from './tokens';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeng/themes';
import Lara from '@primeng/themes/lara';
import { MessageService, ConfirmationService } from 'primeng/api';
import { MsalModule, MsalService, MsalGuard, MsalBroadcastService, MSAL_INSTANCE, MSAL_GUARD_CONFIG, MSAL_INTERCEPTOR_CONFIG } from '@azure/msal-angular';
import { MSALInstanceFactory, MSALGuardConfigFactory, MSALInterceptorConfigFactory } from './msal.module-config';
import { environment } from '../environments/environment';
import { defaultHeadersInterceptor } from './services/default-headers.interceptor';
import { authTokenInterceptor } from './services/auth-token.interceptor';
import { AuthService } from './services';
import { Observable, of } from 'rxjs';
import { switchMap, tap, catchError } from 'rxjs/operators';

import { routes } from './app.routes';

/** Lara theme preset using blue as the primary color palette. */
const LaraBlueLight = definePreset(Lara, {
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}'
    }
  },
  components: {
    radiobutton: {
      root: {
        checkedBackground: '{form.field.background}',
        checkedHoverBackground: '{form.field.background}'
      },
      icon: {
        checkedColor: '{primary.color}',
        checkedHoverColor: '{primary.hover.color}'
      }
    }
  }
});

/** Sets the active MSAL account after redirect, then pre-fetches and caches user profile. */
export function MSALAppInitializerFactory(msalService: MsalService, authService: AuthService): () => Observable<void> {
  return () => msalService.handleRedirectObservable().pipe(
    tap(result => {
      if (result) {
        msalService.instance.setActiveAccount(result.account);
      } else {
        const accounts = msalService.instance.getAllAccounts();
        if (accounts.length > 0) {
          msalService.instance.setActiveAccount(accounts[0]);
        }
      }
    }),
    switchMap(() => authService.initUserProfile()),
    catchError(err => {
      console.error('[MSAL] Authentication initialization error:', err);
      const accounts = msalService.instance.getAllAccounts();
      if (accounts.length > 0) {
        msalService.instance.setActiveAccount(accounts[0]);
      }
      return authService.initUserProfile();
    })
  );
}

/** MSAL providers using a custom functional auth interceptor instead of MsalInterceptor. */
const msalProviders = [
  importProvidersFrom(MsalModule),
  { provide: APP_INITIALIZER, useFactory: MSALAppInitializerFactory, multi: true, deps: [MsalService, AuthService] },
  { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory, deps: [PLATFORM_ID] },
  { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
  { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },
  MsalService,
  MsalGuard,
  MsalBroadcastService
];

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    provideAnimationsAsync(),
    provideHttpClient(
      withInterceptors([defaultHeadersInterceptor, authTokenInterceptor])
    ),
    providePrimeNG({
      theme: {
        preset: LaraBlueLight,
        options: {
          darkModeSelector: '.app-dark'
        }
      }
    }),
    MessageService,
    ConfirmationService,
    { provide: ALLOW_IMPERSONATION, useValue: environment.allowImpersonation },

    ...msalProviders
  ]
};
